/**
 * Product Card Quick Add Button
 * Triggers the existing quick-view functionality
 * - Llama directamente al método del custom element <quick-view>
 * - Reutiliza toda la lógica ya implementada en el tema
 * - Detecta el swatch activo para mostrar la variante correcta
 * - Verifica que el drawer existe antes de proceder
 */

class ProductCardQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.productHandle = this.button?.dataset.productHandle;
    
    // Find product card container
    this.productCard = this.closest('product-card') || this.closest('.product-card');
    
    if (this.button && this.productCard) {
      this.button.addEventListener('click', this.handleClick.bind(this));
      this.initSwatchTracking();
    }
  }
  
  /**
   * Initialize swatch tracking to update variant display
   */
  initSwatchTracking() {
    const swatchesContainer = this.productCard.querySelector('.product-card-swatches--scrollable');
    if (!swatchesContainer) return;
    
    const swatches = swatchesContainer.querySelectorAll('.product-card-swatch');
    
    // Listen for swatch hover events to update active state
    swatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', () => {
        // Remove active class from all swatches
        swatches.forEach(s => s.classList.remove('active'));
        // Add active class to hovered swatch
        swatch.classList.add('active');
      });
    });
  }
  
  /**
   * Handle button click - trigger quick view
   * Uses multiple strategies to ensure the quick-view opens correctly
   */
  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    if (this.button.disabled) {
      console.warn('Quick add button is disabled');
      return;
    }
    
    // Verify that the Product Drawer exists in the DOM
    const drawer = document.getElementById('Product-Drawer');
    if (!drawer) {
      console.error('Product drawer (#Product-Drawer) not found in DOM');
      console.log('Redirecting to product page as fallback');
      if (this.productHandle) {
        window.location.href = `/products/${this.productHandle}`;
      }
      return;
    }
    
    // Find the quick-view element in this product card
    const quickViewElement = this.productCard.querySelector('quick-view');
    
    if (quickViewElement) {
      // Verify it has the required data attribute
      if (!quickViewElement.dataset.productHandle) {
        console.warn('Quick view element missing product handle');
        window.location.href = `/products/${this.productHandle}`;
        return;
      }
      
      // Show loading state on our button
      this.button.classList.add('loading');
      
      // Strategy 1: Try to call the method directly (most reliable)
      if (typeof quickViewElement.setupEventListener === 'function') {
        console.log('Triggering quick view via direct method call');
        
        // Create a synthetic event to pass to the method
        const syntheticEvent = new Event('click', {
          bubbles: true,
          cancelable: true
        });
        
        // Call the method directly
        quickViewElement.setupEventListener(syntheticEvent);
        
      } else {
        // Strategy 2: Dispatch a MouseEvent (more complete than simple click)
        console.log('Triggering quick view via MouseEvent dispatch');
        
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: 1
        });
        
        quickViewElement.dispatchEvent(clickEvent);
      }
      
      // Remove loading state after a delay
      // The quick-view element will show its own loading state
      setTimeout(() => {
        this.button.classList.remove('loading');
      }, 500);
      
      console.log('Quick view triggered for product:', this.productHandle);
      
    } else {
      console.warn('Quick view element not found in product card');
      console.log('This may happen if settings.enable_quick_view is disabled');
      
      // Fallback: navigate to product page
      if (this.productHandle) {
        window.location.href = `/products/${this.productHandle}`;
      }
    }
  }
}

// Register custom element
if (!customElements.get('quick-add-button')) {
  customElements.define('quick-add-button', ProductCardQuickAdd);
}

// Re-initialize on dynamic content load (Shopify theme editor, filters, etc)
document.addEventListener('shopify:section:load', () => {
  console.log('Quick add buttons reinitialized on section load');
});

// Debug helper (remove in production)
if (window.location.search.includes('debug')) {
  console.log('Quick add button custom element registered');
}
